<template>
  <div :id="content.idHTML" :class="jel_getClassObject(content)">
    <div
      v-if="isLoading"
      class="spinner-fullscreen d-flex flex-column justify-content-center align-items-center text-center"
    >
      <b-spinner variant="primaire" :label="$t('chargement')"></b-spinner>
    </div>
    <!-- Fait la validation du formulaire si le idProfession a été mis à jour dans le store -->
    <!-- tag="div" nécessaire pour accessibilité, éviter div dans un span -->
    <ValidationProvider rules="required" tag="div" class="d-none">
      <label for=""></label>
      <input v-model="idProfession" type="hidden" />
    </ValidationProvider>

    <Titre :content="contentTitre"></Titre>
    <Paragraphe :content="contentParagraphe"></Paragraphe>

    <!------- Situation professionnelle ------->
    <ListeChoix
      v-model="idChoixSituation"
      class="my-0 w-100"
      :content="contentSituationProfessionnelle"
    />
    <Hyperlien :content="contentAide" class="mt-n1 mb-2" />

    <!------- Profession ------->
    <div v-if="afficheProfession">
      <ListeChoix v-model="professionChoisie" class="mt-0 w-100" :content="contentProfession" />
    </div>

    <!------- Secteur d'activité ------->
    <div v-if="afficheSecteur">
      <ListeChoix v-model="secteurChoisi" class="mt-0 w-100" :content="contentSecteurActivite" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import Hyperlien from '@/components/base/Hyperlien';
import ListeChoix from '@/components/formulaire/base/ListeChoix.vue';

import Titre from '@/components/base/Titre';
import Paragraphe from '@/components/base/Paragraphe';

import { PageMapping } from '@/helpers/PageMappingHelpers';
import profession from '@/services/api/compte/profession';
import { getQueryParamsValue } from '@/helpers/AppHelpers';

export default {
  name: 'FormulaireProfession',
  components: { Hyperlien, ListeChoix, Titre, Paragraphe },
  props: { content: { type: Object, required: true } },
  data() {
    return {
      isLoading: true,
      choixSituation: '',

      choixProfession: '',
      tableauProfession: [],
      afficheProfession: false,

      choixSecteur: '',
      tableauSecteursProfessionChoisie: [],
      afficheSecteur: false,
    };
  },
  computed: {
    contentAide() {
      return {
        texte: this.content.libExplication,
        type: 'aide',
        lien: {
          idAide: this.content.idQuestion,
        },
      };
    },
    contentParagraphe() {
      if (this.getSpFromQueryParams() == 1) {
        return {
          richText: this.content.richTextSp,
        };
      }
      return {
        richText: this.content.richTextParDefaut,
      };
    },
    contentTitre() {
      if (this.getSpFromQueryParams() == 1) {
        return {
          titre: this.content.titreSp,
          type: this.content.typeSp,
          classeCSS: this.content.classeCSSSp,
        };
      }
      return {
        titre: this.content.titreParDefaut,
        type: this.content.typeParDefaut,
        classeCSS: this.content.classeCSSParDefaut,
      };
    },
    contentSituationProfessionnelle() {
      // doit conserver id même si non utilisé sinon name = undefined
      const listeChoix = Object.entries(this.listeProfessions).map(([id, name]) => {
        return { value: name.id, option: name.name };
      });
      return {
        libChoix: this.content.libSituationProfessionnelle,
        listeChoix,
        optionChoisie: this.idChoixSituation || '',
        placeholderSelection: this.content.placeholderSituationProfessionnelle,
      };
    },
    contentProfession() {
      return {
        activerRecherche: 'true',
        libChoix: this.content.libProfession,
        listeChoix: this.tableauProfession,
        optionChoisie: this.professionChoisie || '',
        placeholderSelection: this.content.placeholderProfession,
        utiliserModaleMobile: 'true',
      };
    },
    contentSecteurActivite() {
      return {
        libChoix: this.content.libSecteurActivite,
        listeChoix: this.tableauSecteursProfessionChoisie,
        optionChoisie: this.secteurChoisi || '',
        placeholderSelection: this.content.placeholderSecteurActivite,
      };
    },
    idChoixSituation: {
      get() {
        return this.$store.state.compte.choixProfession.idSituationProfessionnelle;
      },
      set(value) {
        this.$store.commit('compte/SET_ID_SITUATION_PROFESSIONNELLE', value);
        this.situationChoisie = this.listeProfessions.find((element) => element.id === value);
      },
    },
    idProfession: {
      get() {
        return this.$store.state.compte.choixProfession.idProfession;
      },
      set(value) {
        this.$store.commit('compte/SET_ID_PROFESSION', value);
      },
    },
    secteurActivite: {
      get() {
        return this.$store.state.compte.choixProfession.secteurActivite;
      },
      set(value) {
        this.$store.commit('compte/SET_ID_SECTEUR_ACTIVITE', value);
      },
    },
    situationChoisie: {
      get() {
        return this.choixSituation;
      },
      set(value) {
        this.choixSituation = value;
        this.mettreAJourSituation();
      },
    },

    professionChoisie: {
      get() {
        return this.choixProfession;
      },
      set(value) {
        if (typeof value === 'string') {
          this.choixProfession = this.situationChoisie?.occupations
            ? Object.entries(this.situationChoisie.occupations)
                .map(([titre, professions]) => {
                  return { value: titre, option: titre, professions };
                })
                .find((item) => item.value === value)
            : '';
        } else {
          this.choixProfession = value;
        }
        this.mettreAJourProfession();
      },
    },
    secteurChoisi: {
      get() {
        return this.choixSecteur;
      },
      set(value) {
        this.choixSecteur = value;
        this.mettreAJourSecteur();
      },
    },
    listeProfessions: {
      get() {
        return this.$store.state.compte.listeProfessions;
      },
    },
  },
  async created() {
    profession.getListeProfessions().catch(() => {
      this.$router.push(PageMapping.ERREUR_IDENTITE_PROFESSION.siteUrl).catch(() => {
        console.error(`Route inexistante: ${PageMapping.ERREUR_IDENTITE_PROFESSION.siteUrl}`);
      });
    });

    if (this.idChoixSituation && this.idChoixSituation != '') {
      const situationsProfessionnelles = this.listeProfessions.filter(
        (s) => s.id === this.idChoixSituation
      );
      const { idProfession } = this;
      // remplit la situation professionnelle
      this.situationChoisie =
        situationsProfessionnelles.length == 0 ? {} : situationsProfessionnelles[0];
      // idProfession vidé dans le set de professionChoisie si on a multiples professions
      // doit reforcer le id au store pour validation provider
      this.idProfession = idProfession;

      // remplir la liste de tous les secteurs reliés a une profession
      if (
        this.situationChoisie?.occupations &&
        Object.values(this.situationChoisie.occupations).length > 1
      ) {
        const secteursProfession = Object.entries(this.situationChoisie.occupations)
          .map(([titre, secteursProfession]) => {
            return { titre, professions: secteursProfession };
          })
          .find((p) => {
            // va récupérer la profession par le id du store
            let professionStore = p.professions.filter((pr) => pr.id === this.idProfession);
            if (professionStore.length == 0) {
              return false;
            }
            professionStore = {
              titre: professionStore[0].title,
              professions: professionStore,
            };
            return true;
          });
        if (secteursProfession) {
          this.professionChoisie = {
            value: secteursProfession.titre,
            option: secteursProfession.titre,
            professions: secteursProfession.professions,
          };
          // idProfession vidé dans le set de professionChoisie si on a multiples secteurs
          // doit reforcer le id au store pour validation provider
          this.idProfession = idProfession;
          // relancer getTableau pour assigner la valeur du secteur dans son champs
          this.getTableauSecteursProfessionChoisie();
        }
      }
    }
    this.isLoading = false;
  },
  methods: {
    ...mapActions('compte', ['getListeProfessions']),
    getTableauSecteursProfessionChoisie() {
      if (this.professionChoisie?.professions) {
        this.tableauSecteursProfessionChoisie = [];
        for (let i = 0; i < this.professionChoisie.professions.length; i++) {
          this.tableauSecteursProfessionChoisie.push({
            value: this.professionChoisie.professions[i].id,
            option: this.professionChoisie.professions[i].industry.name,
            secteurActivite: this.professionChoisie.professions[i].industry.id,
          });
          if (this.tableauSecteursProfessionChoisie[i].value === this.idProfession) {
            this.secteurChoisi = this.tableauSecteursProfessionChoisie[i];
          }
        }
      }
    },

    // Méthode pour récupérer le paramètre "sp" permettant d'afficher du contenu ciblé (ie pour les joueurs sans profession")
    getSpFromQueryParams() {
      return getQueryParamsValue('sp') ?? 0;
    },

    mettreAJourSituation() {
      if (this.situationChoisie?.occupations) {
        this.tableauProfession = Object.entries(this.situationChoisie.occupations).map(
          ([titre, professions]) => {
            return { value: titre, option: titre, professions };
          }
        );
      } else {
        this.tableauProfession = [];
      }

      if (this.tableauProfession.length > 1) {
        this.afficheProfession = true;
      } else {
        this.idProfession = this.tableauProfession[0]?.professions[0].id;
        this.secteurActivite = this.tableauProfession[0]?.professions[0].industry.id;
        this.afficheProfession = false;
        this.afficheSecteur = false;
      }
      this.tableauSecteursProfessionChoisie = [];
    },

    mettreAJourProfession() {
      if (this.professionChoisie) {
        if (this.professionChoisie.professions.length > 1) {
          this.idProfession = '';
          this.secteurActivite = '';
          this.secteurChoisi = '';
          this.afficheSecteur = true;
          this.getTableauSecteursProfessionChoisie();
        } else {
          this.afficheSecteur = false;
          this.idProfession = this.professionChoisie.professions[0].id;
          this.secteurActivite = this.professionChoisie.professions[0].industry.id;
          this.tableauSecteursProfessionChoisie = [];
        }
      } else {
        this.idProfession = '';
        this.secteurActivite = '';
        this.secteurChoisi = '';
        this.afficheSecteur = false;
        this.tableauSecteursProfessionChoisie = [];
      }
    },

    mettreAJourSecteur() {
      this.idProfession = this.secteurChoisi;
      const valeur =
        typeof this.secteurChoisi === 'object' ? this.secteurChoisi?.value : this.secteurChoisi;
      this.secteurActivite =
        this.tableauSecteursProfessionChoisie.find((s) => s.value === valeur)?.secteurActivite ??
        '';
    },
  },
};
</script>
